import { Component, Suspense, type ComponentType, type ReactNode } from 'react';
import { useLocale } from '../../i18n';
import { lazyWithReload } from '../lazyWithReload';
import type { KMViewsProps } from './KMViews';
import type { SeriespillViewsProps } from './SeriespillViews';
import type { TournamentWizardViewsProps } from './TournamentWizardViews';
import type { AmericanoSetupViewsProps } from './AmericanoSetupViews';
import type { AmericanoLiveViewsProps } from './AmericanoLiveViews';
import type { InfoViewsProps } from './InfoViews';

// ─────────────────────────────────────────────────────────────────────
// THE VIEW GROUPS THAT LOAD ON DEMAND (#1145, AUD-035).
//
// One 4.2 MB main chunk carried every screen to every visitor: a player on a
// phone downloaded the tournament wizard, the KM and seriespill engines and
// every americano setup. These six groups are the portal's tournament and
// reference screens; each now arrives in its own chunk the first time one of
// its views is opened.
//
// SAME NAMES, SAME PROPS — AppShell renders these exactly where it rendered
// the eager groups. What changes is that a group is not MOUNTED outside its
// views (it rendered nothing there anyway), because mounting is what would
// fetch its chunk.
//
// THE VIEW SETS ARE THE GROUPS' OWN. Each is exactly the `view === '…'`
// comparisons in that group's source; `app-lazy-view-groups.test.ts` reads
// the source with the TypeScript parser and fails if a group answers for a
// view its set here does not name — which would otherwise be a screen that
// silently never appears. Only groups that render NOTHING outside their views
// and hold no hooks are here: LibraryViews (the seriespill history modal) and
// ReportingViews (loads its settings on mount) are not, on purpose.
// ─────────────────────────────────────────────────────────────────────

export const LAZY_VIEW_GROUPS = {
  KMViews: ['klubbmesterskap', 'klubbmesterskapSetup'],
  SeriespillViews: ['seriespill', 'seriespillSetup'],
  TournamentWizardViews: ['tournament', 'wizard'],
  AmericanoSetupViews: [
    'americanoSetup',
    'clubAmericanoSetup',
    'clubMexicanoSetup',
    'clubTeamAmericanoSetup',
    'mexicanoSetup',
    'mixAmericanoSetup',
    'mixicanoSetup',
    'teamAmericanoSetup',
    'teamMexicanoSetup',
  ],
  AmericanoLiveViews: [
    'americano',
    'clubAmericano',
    'clubMexicano',
    'clubTeamAmericano',
    'mexicano',
    'mixAmericano',
    'mixicano',
    'teamMexicano',
  ],
  InfoViews: ['changelog', 'engine', 'help'],
} as const satisfies Record<string, readonly string[]>;

/** What shows for the moment a group's chunk is on its way. */
function ViewLoading() {
  const { t } = useLocale();
  return (
    <div role="status" aria-busy="true" className="py-16 text-center text-sm text-ink-3">
      {t('app.loading')}
    </div>
  );
}

/**
 * A group whose chunk could not be loaded, after the one automatic reload.
 *
 * Without this the rethrown error would unmount the WHOLE app — a blank page
 * for one screen that failed. This keeps the failure to the screen, says so,
 * and offers the same reload the boot watchdog does.
 */
function ViewLoadFailed() {
  const { t } = useLocale();
  return (
    <div role="alert" className="py-16 text-center text-sm text-ink-2 space-y-3">
      <p>{t('app.viewLoadFailed')}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="px-4 py-2 rounded-full bg-ink text-white text-sm font-semibold"
      >
        {t('app.reload')}
      </button>
    </div>
  );
}

class ViewBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    return this.state.failed ? <ViewLoadFailed /> : this.props.children;
  }
}

function onDemand<P extends { view: string }>(
  views: readonly string[],
  load: () => Promise<{ default: ComponentType<P> }>,
) {
  // `React.lazy`'s props type does not survive a generic `P`; it renders exactly
  // the component `load` resolves to.
  const Lazy = lazyWithReload(load) as unknown as ComponentType<P>;
  const set = new Set(views);
  return function OnDemandGroup(props: P) {
    if (!set.has(props.view)) return null;
    return (
      <ViewBoundary>
        <Suspense fallback={<ViewLoading />}>
          <Lazy {...props} />
        </Suspense>
      </ViewBoundary>
    );
  };
}

export const KMViews = onDemand<KMViewsProps>(LAZY_VIEW_GROUPS.KMViews, () =>
  import('./KMViews').then((m) => ({ default: m.KMViews })),
);
export const SeriespillViews = onDemand<SeriespillViewsProps>(
  LAZY_VIEW_GROUPS.SeriespillViews,
  () => import('./SeriespillViews').then((m) => ({ default: m.SeriespillViews })),
);
export const TournamentWizardViews = onDemand<TournamentWizardViewsProps>(
  LAZY_VIEW_GROUPS.TournamentWizardViews,
  () => import('./TournamentWizardViews').then((m) => ({ default: m.TournamentWizardViews })),
);
export const AmericanoSetupViews = onDemand<AmericanoSetupViewsProps>(
  LAZY_VIEW_GROUPS.AmericanoSetupViews,
  () => import('./AmericanoSetupViews').then((m) => ({ default: m.AmericanoSetupViews })),
);
export const AmericanoLiveViews = onDemand<AmericanoLiveViewsProps>(
  LAZY_VIEW_GROUPS.AmericanoLiveViews,
  () => import('./AmericanoLiveViews').then((m) => ({ default: m.AmericanoLiveViews })),
);
export const InfoViews = onDemand<InfoViewsProps>(LAZY_VIEW_GROUPS.InfoViews, () =>
  import('./InfoViews').then((m) => ({ default: m.InfoViews })),
);
